<template>
  <div class="switch-toggle">
    <!-- use a hidden input to submit the switch state, since the switch is represented by a button (not a checkbox) -->
    <input :name="fieldName" :value="modelValue ? 1 : 0" type="hidden" />
    <button
      :id="id"
      ref="input"
      type="button"
      :aria-label="ariaLabel"
      :aria-labelledby="ariaLabelledby || undefined"
      :aria-describedby="ariaDescribedby || undefined"
      :aria-pressed="modelValue"
      :aria-disabled="ariaDisabled ? 'true' : undefined"
      :disabled="disabled"
      class="switch-toggle__button"
      @click="update"
    />
    <span class="switch-toggle__label" style="color: inherit" aria-hidden="true">{{ offLabel ?? t('common.off') }}</span>
    <span class="switch-toggle__label" style="color: inherit" aria-hidden="true">{{ onLabel ?? t('common.on') }}</span>
  </div>
</template>

<script setup>
import { common } from 'vue_features/shared/i18n'
import { namespaceLocaleObj } from 'vue_features/shared/helpers/i18n_helper'
import uniqueId from 'lodash/uniqueId'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'

// We expose explicit ariaLabel / ariaLabelledby / ariaDescribedby props so
// these a11y attributes land on the inner <button> (where they belong)
// instead of on the wrapper <div> that fallthrough attrs would target. We
// intentionally leave inheritAttrs at its default so existing callers can
// still pass `class`, `data-test`, etc. through to the root <div>.
const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  // Use ariaDisabled instead of `disabled` when the toggle should remain
  // focusable (so screen-reader users can hear the disabled-reason via
  // aria-describedby and any LzTooltip on focus). Click is a no-op when set.
  ariaDisabled: {
    type: Boolean,
    default: false,
  },
  ariaLabel: {
    type: String,
    default: undefined,
  },
  ariaLabelledby: {
    type: String,
    default: null,
  },
  ariaDescribedby: {
    type: String,
    default: null,
  },
  id: {
    type: String,
    default: function () {
      return uniqueId('switch-toggle-')
    },
  },
  fieldName: {
    type: String,
    default: null,
  },
  offLabel: {
    type: String,
    default: null,
  },
  onLabel: {
    type: String,
    default: null,
  },
})
const { t } = useI18n({ messages: namespaceLocaleObj(common, 'common', { only: ['off', 'on'] }) })

const input = ref(null)
const emit = defineEmits(['update:modelValue'])
const update = () => {
  if (props.disabled || props.ariaDisabled) return
  emit('update:modelValue', !props.modelValue)
}
</script>
